import { useState } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import LookupSelect from '../components/LookupSelect'

interface LinhaXmlNFe {
  uf: string | null
  data: string
  cnpj: string
  serie: string
  numero: number
  chave: string
  natureza: number
  tabela: 'N' | 'F' | 'P'
}

interface LinhaSelecionavel extends LinhaXmlNFe {
  selecionado: boolean
}

function hojeISO() { return new Date().toISOString().slice(0, 10) }
function umMesAtrasISO() {
  const d = new Date()
  d.setMonth(d.getMonth() - 1)
  return d.toISOString().slice(0, 10)
}
function fmtDataBr(v: string) { return v ? v.slice(0, 10).split('-').reverse().join('/') : '-' }
function fmtCnpj(doc: string) {
  const n = (doc || '').replace(/\D/g, '').padStart(14, '0')
  return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
}

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.85rem', height: 32, background: '#fff',
}
const label: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }

function Campo({ rotulo, width, children }: { rotulo: string; width?: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

export default function GravarXmlNFe() {
  const { empresaCod } = useEmpresa()
  const [dataInicio, setDataInicio] = useState(umMesAtrasISO())
  const [dataFim, setDataFim] = useState(hojeISO())
  const [indope, setIndope] = useState('0')
  const [numero, setNumero] = useState('')
  const [fornecedorCod, setFornecedorCod] = useState('')
  const [fornecedorDes, setFornecedorDes] = useState('')
  const [naturezaCod, setNaturezaCod] = useState('')

  const [buscando, setBuscando] = useState(false)
  const [erro, setErro] = useState('')
  const [gerando, setGerando] = useState(false)
  const [linhas, setLinhas] = useState<LinhaSelecionavel[]>([])

  async function buscar() {
    setErro(''); setBuscando(true); setLinhas([])
    try {
      const params: Record<string, string | number> = {
        empcod: empresaCod ?? 1, dataInicio, dataFim, indope,
      }
      if (numero.trim()) params.numero = Number(numero)
      if (fornecedorCod) params.cnpj = fornecedorCod
      if (naturezaCod.trim()) params.naturezaCod = Number(naturezaCod)
      const r = await api.get('/erp/xml-nfe/lista', { params })
      const dados: LinhaXmlNFe[] = r.data
      setLinhas(dados.map(l => ({ ...l, selecionado: false })))
      if (dados.length === 0) setErro('Nenhuma NF-e com XML disponível encontrada nesse período.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao buscar notas.')
    } finally {
      setBuscando(false)
    }
  }

  function marcarTodos(v: boolean) {
    setLinhas(prev => prev.map(l => ({ ...l, selecionado: v })))
  }
  function toggleLinha(idx: number) {
    setLinhas(prev => prev.map((l, i) => i === idx ? { ...l, selecionado: !l.selecionado } : l))
  }

  async function baixarSelecionados() {
    const selecionadas = linhas.filter(l => l.selecionado)
    if (selecionadas.length === 0) { setErro('Marque ao menos uma NF-e.'); return }
    setErro(''); setGerando(true)
    try {
      const resp = await api.post('/erp/xml-nfe/gerar',
        { notas: selecionadas.map(l => ({ chave: l.chave, tabela: l.tabela })) },
        { responseType: 'blob' },
      )
      const url = window.URL.createObjectURL(new Blob([resp.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `xml-nfe-${Date.now()}.zip`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao gerar o arquivo ZIP.')
    } finally {
      setGerando(false)
    }
  }

  const todasMarcadas = linhas.length > 0 && linhas.every(l => l.selecionado)
  const qtdMarcadas = linhas.filter(l => l.selecionado).length

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.4rem' }}>Gravar Arquivos XML - NF-e</h2>
      <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
        Baixa o XML das NF-e do período selecionadas num arquivo .zip.
      </p>

      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Campo rotulo="Período De" width={150}>
            <input style={inp} type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
          </Campo>
          <Campo rotulo="Até" width={150}>
            <input style={inp} type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
          </Campo>
          <Campo rotulo="Nota de" width={120}>
            <select style={inp} value={indope} onChange={e => setIndope(e.target.value)}>
              <option value="0">Entrada</option>
              <option value="1">Saída</option>
            </select>
          </Campo>
          <Campo rotulo="Nº da Nota (opcional)" width={130}>
            <input style={inp} inputMode="numeric" value={numero} onChange={e => setNumero(e.target.value.replace(/\D/g, ''))} placeholder="Todas" />
          </Campo>
          <Campo rotulo="Fornecedor (opcional)" width={280}>
            <LookupSelect
              endpoint="/erp/clientes/lista" codKey="cli_cod" descKey="cli_fan"
              codValue={fornecedorCod} descValue={fornecedorDes}
              onSelect={(cod, des) => { setFornecedorCod(cod); setFornecedorDes(des) }}
              placeholder="Buscar fornecedor..." listWidth={320} somenteComTexto
            />
          </Campo>
          <Campo rotulo="Natureza (código, opcional)" width={150}>
            <input style={inp} inputMode="numeric" value={naturezaCod} onChange={e => setNaturezaCod(e.target.value.replace(/\D/g, ''))} placeholder="Todas" />
          </Campo>
          <button className="btn btn-primary btn-sm" disabled={buscando} onClick={buscar} style={{ height: 32 }}>
            {buscando ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}

      {linhas.length > 0 && (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem',
        }}>
          <button className="btn btn-primary btn-sm" onClick={baixarSelecionados} disabled={gerando} style={{ height: 32, marginBottom: '0.9rem' }}>
            {gerando ? 'Gerando...' : `Baixar XML(s) Selecionado(s) (${qtdMarcadas})`}
          </button>

          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', fontSize: '0.78rem', marginBottom: '0.5rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'center', width: 40 }}>
                    <input type="checkbox" checked={todasMarcadas} onChange={e => marcarTodos(e.target.checked)} />
                  </th>
                  <th style={{ textAlign: 'center', width: 45 }}>UF</th>
                  <th style={{ textAlign: 'center', width: 90 }}>Data</th>
                  <th style={{ textAlign: 'left', width: 160 }}>CNPJ</th>
                  <th style={{ textAlign: 'center', width: 70 }}>Série</th>
                  <th style={{ textAlign: 'center', width: 90 }}>Número</th>
                  <th style={{ textAlign: 'left' }}>Chave</th>
                  <th style={{ textAlign: 'center', width: 90 }}>Natureza</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l, idx) => (
                  <tr key={l.chave} style={{ background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={l.selecionado} onChange={() => toggleLinha(idx)} />
                    </td>
                    <td style={{ textAlign: 'center', color: '#6b7280', fontWeight: 600 }}>{l.uf}</td>
                    <td style={{ textAlign: 'center', color: '#374151' }}>{fmtDataBr(l.data)}</td>
                    <td style={{ fontFamily: 'monospace', color: '#374151' }}>{fmtCnpj(l.cnpj)}</td>
                    <td style={{ textAlign: 'center', color: '#374151' }}>{l.serie}</td>
                    <td style={{ textAlign: 'center', fontWeight: 700, color: '#1a6ea8' }}>{l.numero}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#1d4ed8' }}>{l.chave}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '1px 8px', borderRadius: 10,
                        fontSize: '0.72rem', fontWeight: 600, background: '#eef2f7', color: '#334155',
                      }}>
                        {l.natureza}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
